import { ActionableNotification, InlineNotification } from '@carbon/react';
import styles from './bulk-upload.module.scss';
import { errorLines } from './fields';
import type { RequestFailure } from './hooks';

interface RequestFailureBannerProps {
  failure: RequestFailure;
  /** Sends the same request again. Absent: no Try again button. */
  onTryAgain?: () => void;
  /** What the request did to the assets, for the title. Default: created */
  action?: 'created' | 'retried';
}

/**
 * A submit or retry request that failed as a whole. Per-row errors are
 * listed; anything else offers Try again, with the server's reason when it
 * gave one.
 * @param {RequestFailureBannerProps} props - The failure and Try again
 * @return {JSX.Element} The banner
 */
export const RequestFailureBanner = ({
  failure,
  onTryAgain,
  action = 'created',
}: RequestFailureBannerProps) => {
  if (failure.kind === 'rows') {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title={`Some assets have invalid values and were not ${action}.`}
      >
        <ul className={styles.failureList}>
          {failure.rows.map(({ position, errors }) => (
            <li key={position}>
              Row {position}: {errorLines(errors).join('; ')}
            </li>
          ))}
        </ul>
      </InlineNotification>
    );
  }
  return (
    <ActionableNotification
      inline
      kind="error"
      lowContrast
      hideCloseButton
      title={`Assets could not be ${action}. Try again.`}
      subtitle={failure.message ?? undefined}
      {...(onTryAgain && {
        actionButtonLabel: 'Try again',
        onActionButtonClick: onTryAgain,
      })}
    />
  );
};
